import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TermInsteadView, TermPlaceView, TermStatus, TermsView } from '../../types/agent';
import { CellText } from './CellLink';

const ORDER: TermStatus[] = ['gap', 'ambiguous', 'to judge', 'not in the model', 'matches', 'located'];
type SheetState = 'idle' | 'writing' | 'done' | 'error';

/** A term sheet against the model (sculpt-api PLAN-pf-skills.md §10, `term-sheet-reconciliation`), folded behind one
 *  button under the model's account of the gaps until the user opens it: the answer gives the gaps, the card is every
 *  term `reconcile_terms` compared. Opened: the terms by status, the gaps first — each with the model's value and a
 *  link selecting it, then, muted, the document's value and the gap or the tool's note — and the terms the document
 *  does not state; a term not in the model, what the model holds in its place when the tool says it. Folded or open, a button writes every term to a Term Sheet Gaps sheet. Drawn from the tool's card,
 *  so the list is complete and costs no tokens. A place the gap or the note names is a link too (`CellText`). */
export function TermsCard({ view, onShowCell, sheets, onWriteSheet }: {
  view: TermsView;
  onShowCell?: (sheet: string, cell: string) => void;
  /** The workbook's sheet names: a place in a term's note is a link only when it names one of them. */
  sheets?: readonly string[];
  /** Writes the Term Sheet Gaps sheet; absent in a read-only thread. */
  onWriteSheet?: (view: TermsView) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState<SheetState>('idle');
  const write = async () => {
    if (!onWriteSheet) return;
    setSheet('writing');
    try {
      await onWriteSheet(view);
      setSheet('done');
    } catch {
      setSheet('error');
    }
  };
  // The sheet is offered folded or open: the answer says the card can write it, so the button is in sight.
  const footer = onWriteSheet && view.rows.length > 0 ? (
    <div className="border-t border-line-row px-3 py-2">
      {sheet === 'done' ? (
        <span className="text-[11.5px] text-accent" data-testid="terms-sheet-done">✓ {t('terms.sheetDone')}</span>
      ) : (
        <button type="button" data-testid="terms-write-sheet" disabled={sheet === 'writing'} onClick={() => void write()}
          className="rounded-sm border border-line-btn px-2.5 py-1 text-[11.5px] text-ink hover:bg-hover disabled:opacity-50">
          {sheet === 'writing' ? t('terms.sheetWriting') : t('terms.writeSheet')}
        </button>
      )}
      {sheet === 'error' && <span className="ml-2 text-[11.5px] text-danger">{t('terms.sheetFailed')}</span>}
    </div>
  ) : null;
  const gaps = view.rows.filter((r) => r.status === 'gap').length;
  if (!open) {
    return (
      <div className="animate-pc-in-enter overflow-hidden rounded-lg border border-line-card bg-white" data-testid="terms-card">
        <button type="button" data-testid="terms-open" onClick={() => setOpen(true)}
          className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[12px] text-ink hover:bg-hover">
          <span className="text-muted">▸</span>
          <span>{t('terms.show', { gaps, total: view.rows.length })}</span>
        </button>
        {footer}
      </div>
    );
  }
  return (
    <div className="animate-pc-in-enter overflow-hidden rounded-lg border border-line-card bg-white" data-testid="terms-card">
      <div className="border-b border-line-row px-3 py-2">
        <button type="button" data-testid="terms-close" onClick={() => setOpen(false)}
          className="flex w-full items-center gap-1.5 text-left text-[12.5px] font-medium text-ink">
          <span className="text-muted">▾</span>
          <span>{t('terms.title')}</span>
        </button>
      </div>

      {ORDER.map((status) => {
        const rows = view.rows.filter((r) => r.status === status);
        if (!rows.length) return null;
        return (
          <div key={status} className="border-t border-line-row py-1 first:border-t-0" data-testid={`terms-status-${status.replace(/ /g, '-')}`}>
            <div className="px-3 pt-1 text-[11.5px] font-medium text-ink">{t(`terms.status.${status}`)}</div>
            {rows.map((r, i) => {
              const name = r.qualifier ? `${r.term} · ${r.qualifier}` : r.term;
              const first = r.model[0];
              const detail = [
                r.document ? `${t('terms.document')} ${r.document}` : null,
                r.gap ? t('terms.gap', { gap: r.gap }) : null,
                first?.computed ? t('terms.computed') : null,
                r.note,
              ].filter(Boolean).join(' · ');
              return (
                <div key={`${r.term}-${r.qualifier ?? ''}-${i}`} data-testid="terms-row">
                  <div className="flex min-w-0 items-baseline gap-1.5 py-0.5 pl-3 pr-3 text-[11.5px]">
                    <span className="min-w-0 flex-1 truncate text-ink" title={name}>{name}</span>
                    {first && <Value text={first.value ?? '—'} />}
                    {first && <Place cell={first} onSelect={onShowCell} />}
                  </div>
                  {r.model.length > 1 && (
                    <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 pl-5 pr-3 text-[10.5px] text-muted" data-testid="terms-candidates">
                      {r.model.slice(1).map((c) => (
                        <span key={`${c.sheet}!${c.address}`} className="flex items-baseline gap-1">
                          <span className="font-mono">{c.value ?? '—'}</span>
                          <Place cell={c} onSelect={onShowCell} />
                        </span>
                      ))}
                    </div>
                  )}
                  {detail && <div className="pl-5 pr-3 text-[10.5px] text-muted" data-testid="terms-detail"><CellText text={detail} sheets={sheets} onSelectRange={onShowCell} /></div>}
                  {r.instead && r.instead.length > 0 && <Instead cells={r.instead} onSelect={onShowCell} />}
                </div>
              );
            })}
          </div>
        );
      })}

      {view.not_stated.length > 0 && (
        <div className="border-t border-line-row px-3 py-1.5 text-[11px] text-muted" data-testid="terms-not-stated">
          {t('terms.notStated', { names: view.not_stated.join(', ') })}
        </div>
      )}

      {footer}
    </div>
  );
}

/** What the model holds in the place of a term it does not hold: each with its label, its value and a link. */
function Instead({ cells, onSelect }: { cells: TermInsteadView[]; onSelect?: (sheet: string, address: string) => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 pl-5 pr-3 text-[10.5px] text-muted" data-testid="terms-instead">
      <span>{t('terms.instead')}</span>
      {cells.map((c) => (
        <span key={`${c.sheet}!${c.address}`} className="flex min-w-0 items-baseline gap-1">
          <span className="truncate text-ink" title={c.label}>{c.label}</span>
          <span className="shrink-0 font-mono text-ink">{c.value ?? '—'}</span>
          <Place cell={c} onSelect={onSelect} />
        </span>
      ))}
    </div>
  );
}

function Value({ text }: { text: string }) {
  return <span className="shrink-0 font-mono text-[11px] text-ink" title={text}>{text.length > 28 ? `${text.slice(0, 27)}…` : text}</span>;
}

function Place({ cell, onSelect }: { cell: Pick<TermPlaceView, 'sheet' | 'address'>; onSelect?: (sheet: string, address: string) => void }) {
  if (!cell.address) return null;
  const label = `${cell.sheet}!${cell.address}`;
  if (!onSelect) return <span className="max-w-[40%] shrink-0 truncate font-mono text-[10.5px] text-muted" title={label}>{label}</span>;
  return (
    <button type="button" data-testid="terms-place" onClick={() => onSelect(cell.sheet, cell.address)} title={label}
      className="max-w-[40%] shrink-0 truncate font-mono text-[10.5px] text-accent underline-offset-2 hover:underline">
      {label}
    </button>
  );
}
